/**
 * Wall face-sketch geometry helpers.
 *
 * Wall shape profiles (BIM "Edit Profile") and sketched profile voids are
 * stored in the host wall's LOCAL sketch frame, not in world space, so they
 * follow the wall when it moves or stretches:
 *   u = meters along the wall run, measured from geometryData.start
 *   v = meters above the wall base (geometryData.baseOffset)
 * The third local axis (w) is the wall's horizontal normal — the sweep axis
 * across the wall thickness.
 *
 * World mapping is a strict right-handed basis:
 *   local X (u) -> wall direction (dx, dy, 0)
 *   local Y (v) -> world up     (0, 0, 1)
 *   local Z (w) -> wall normal  (dy, -dx, 0)
 */

import * as THREE from 'three';

/** Minimal context a renderer/tool needs to map wall-local points to world. */
export interface WallFrameContext {
    /** Wall location-line start (geometryData.start). */
    start: THREE.Vector3;
    /** Wall location-line end (geometryData.end). */
    end: THREE.Vector3;
    /** Wall base elevation in world Z (geometryData.baseOffset). */
    baseZ: number;
    /** Optional plan-view cut height in wall-local v meters: geometry above
     *  this line is clipped away (BIM plan cut-plane truncation). */
    cutMaxV?: number;
}

/**
 * Right-handed basis matrix mapping wall-local (u, v, w) meters to world
 * space. The wall's start/end may carry arbitrary Z — only the plan
 * direction matters for the frame; elevation is carried by baseZ + v.
 */
export function wallBasisMatrix(ctx: WallFrameContext): THREE.Matrix4 {
    const dx = ctx.end.x - ctx.start.x;
    const dy = ctx.end.y - ctx.start.y;
    const len = Math.hypot(dx, dy);
    // Degenerate wall: fall back to +X so the matrix stays invertible.
    const ux = len > 1e-9 ? dx / len : 1;
    const uy = len > 1e-9 ? dy / len : 0;
    const xAxis = new THREE.Vector3(ux, uy, 0);
    const yAxis = new THREE.Vector3(0, 0, 1);
    const zAxis = new THREE.Vector3(uy, -ux, 0);
    const matrix = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
    matrix.setPosition(ctx.start.x, ctx.start.y, ctx.baseZ);
    return matrix;
}

/** Map one wall-local (u, v) point to world (w = 0, the wall mid-plane). */
export function wallLocalToWorld(ctx: WallFrameContext, u: number, v: number): THREE.Vector3 {
    return new THREE.Vector3(u, v, 0).applyMatrix4(wallBasisMatrix(ctx));
}

/** Map a world point onto the wall sketch frame: returns (u, v) meters. */
export function worldToWallLocal(ctx: WallFrameContext, point: THREE.Vector3): { u: number; v: number } {
    const dx = ctx.end.x - ctx.start.x;
    const dy = ctx.end.y - ctx.start.y;
    const len = Math.hypot(dx, dy);
    const ux = len > 1e-9 ? dx / len : 1;
    const uy = len > 1e-9 ? dy / len : 0;
    const u = (point.x - ctx.start.x) * ux + (point.y - ctx.start.y) * uy;
    return { u, v: point.z - ctx.baseZ };
}

/** Horizontal unit normal of the wall run (the sketch-plane normal). */
export function wallFaceNormal(ctx: WallFrameContext): THREE.Vector3 {
    const dx = ctx.end.x - ctx.start.x;
    const dy = ctx.end.y - ctx.start.y;
    const len = Math.hypot(dx, dy);
    const ux = len > 1e-9 ? dx / len : 1;
    const uy = len > 1e-9 ? dy / len : 0;
    return new THREE.Vector3(uy, -ux, 0);
}

/** Build a closed THREE.Shape from a 2D polygon (any winding — ExtrudeGeometry
 *  normalizes holes/faces, and CSG is winding-tolerant). */
export function shapeFromPolygon2D(points: [number, number][]): THREE.Shape {
    const shape = new THREE.Shape();
    shape.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) {
        shape.lineTo(points[i][0], points[i][1]);
    }
    shape.closePath();
    return shape;
}

/** Axis-aligned bounding box of a 2D polygon. */
export function polygonBounds2D(points: [number, number][]): { minU: number; minV: number; maxU: number; maxV: number } {
    let minU = Infinity, minV = Infinity, maxU = -Infinity, maxV = -Infinity;
    for (const [u, v] of points) {
        if (u < minU) minU = u;
        if (v < minV) minV = v;
        if (u > maxU) maxU = u;
        if (v > maxV) maxV = v;
    }
    return { minU, minV, maxU, maxV };
}

/**
 * Sutherland–Hodgman clip of a polygon against the half-plane v <= maxV.
 * Used by the plan-view cut plane: a profile wall (or a sketched void)
 * poking above the cut height must be truncated there, or the CSG would
 * re-cap its openings above the truncation.
 */
export function clipPolygonMaxV(points: [number, number][], maxV: number): [number, number][] {
    if (points.length === 0) return points;
    const output: [number, number][] = [];
    for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const next = points[(i + 1) % points.length];
        const currentIn = current[1] <= maxV;
        const nextIn = next[1] <= maxV;
        if (currentIn) {
            output.push(current);
            if (!nextIn) {
                const t = (maxV - current[1]) / (next[1] - current[1]);
                output.push([current[0] + t * (next[0] - current[0]), maxV]);
            }
        } else if (nextIn) {
            const t = (maxV - current[1]) / (next[1] - current[1]);
            output.push([current[0] + t * (next[0] - current[0]), maxV]);
        }
    }
    return output;
}
